import { authenticationService, egzotechAgentService } from "../../App";
import { ApiError } from "../product-api";

export interface LabelTemplateFields {
    [key: string]: string | number | Date;
}

export type PrinterInfo = {
    portName: null
} | {
    portName: string;
    mediaPresent: boolean;
    mediaType: number;
    mediaWidth: number;
}

interface PrintOptions {
    copies?: number;
    noAutoCut?: boolean;
}

export type LabelData = {
    [key: string]: LabelData;
} & ({
    "/type": "directory";
} | & {
    "/type": "file";
    metadata: { [key: string]: string };
})

const baseUrl = import.meta.env.VITE_LABELS_API ?? "https://labels.internal.egzotech.com/";

export function generate(template: string, fields: LabelTemplateFields | null, overrideMetadata?: { [key: string]: string }, coerceFields?: boolean, svg?: boolean): string;
export function generate(template: File, fields: LabelTemplateFields | null, overrideMetadata?: { [key: string]: string }, coerceFields?: boolean, svg?: boolean): Promise<string>;
export function generate(template: string | File, fields: LabelTemplateFields | null, overrideMetadata?: { [key: string]: string }, coerceFields?: boolean, svg?: boolean): string | Promise<string> {
    const params = new URLSearchParams();

    if (!template) {
        return "";
    }

    setTemplateFields(template, fields, params, coerceFields);

    if (svg) {
        params.set("svg", "");
    }

    if (overrideMetadata) {
        for (const prop in overrideMetadata) {
            params.set("o-" + prop, overrideMetadata[prop]);
        }
    }

    if (typeof template !== "string") {
        const formData = new FormData();
        formData.append("template", template);

        return fetch(`${baseUrl}api/generate/?${params.toString()}`, {
            method: "POST",
            body: formData
        }).then(async (response) => {
            const blob = await response.blob();
            const reader = new FileReader();

            return await new Promise((resolve, reject) => {
                reader.onload = () => {
                    if (typeof reader.result === "string") {
                        resolve(reader.result);
                    }
                }

                reader.onabort = () => {
                    reject("Abort");
                }

                reader.onerror = () => {
                    reject("Error");
                }

                reader.readAsDataURL(blob);
            });
        });
    }

    return `${baseUrl}api/generate/?${params.toString()}`;
}

export async function getLabelIds(path: string): Promise<string[]> {
    const params = new URLSearchParams({
        ids: path.toString()
    })

    const response = await fetch(`${baseUrl}api/labels/?${params.toString()}`, {
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export function labelPreviewUrl(path: string) {
    const params = new URLSearchParams();
    params.set("preview", "");
    params.set("path", path);

    return `${baseUrl}api/labels/?${params.toString()}`;
}

export async function labelsMetadata(template: string) {
    const params = new URLSearchParams();
    params.set("file", template);

    const response = await fetch(`${baseUrl}api/labels/?${params}`);

    if (!response.ok) {
        return null;
    }

    return await response.json();
}

export async function getLabelFiles(): Promise<LabelData | null> {
    const response = await fetch(`${baseUrl}api/labels/`);

    if (!response.ok) {
        return null;
    }

    return await response.json();
}

export async function loadLabels(formData: FormData) {
    try {
        const response = await fetch(`${baseUrl}api/labels/`, {
            method: "POST",
            body: formData
        });

        return response;
    }
    catch(ex) {
        console.warn(ex);
        throw ex;
    }
}

export async function labelsMetadataArray(templates: string[]) {
    const formData = new FormData();
    formData.set("files", JSON.stringify(templates));

    const response = await fetch(`${baseUrl}api/labels/`, {
        method: "POST",
        body: formData
    });

    if (!response.ok) {
        return null;
    }

    return await response.json();
}

export async function addLabelFile(path: string, file: File) {
    const formData = new FormData();
    formData.set("command", "upload");
    formData.set("path", path);
    formData.set("template", file);

    const response = await fetch(`${baseUrl}api/labels/set/`, {
        method: "POST",
        body: formData,
        credentials: "include"
    });

    if (!response.ok) {
        throw new Error(await response.text());
    }

    await response.text();
}

export async function removeLabelFile(path: string) {
    const formData = new FormData();
    formData.set("command", "remove");
    formData.set("path", path);

    const response = await fetch(`${baseUrl}api/labels/set/`, {
        method: "POST",
        body: formData,
        credentials: "include"
    });

    if (!response.ok) {
        throw new Error(await response.text());
    }

    await response.text();
}

export async function slavePrint(slave: string, template: string | File | Blob, fields: LabelTemplateFields | null, options?: PrintOptions, overrideMetadata?: { [key: string]: string }) {
    console.log("Authentication result:", await authenticationService.authenticate(`${baseUrl}/api/user/login/`));

    const params = new FormData();
    const queue: {
        file?: string,
        template?: string,
        fields: { [key: string]: string },
        rawFields?: { [key: string]: string },
        options?: PrintOptions
    }[] = [
        typeof template !== "string" ? {
            file: "file0",
            fields: generateTemplateFieldData(template, fields),
            options
        } : {
            template: template,
            fields: generateTemplateFieldData(template, fields),
            options
        }
    ];

    if (overrideMetadata) {
        queue[0] = typeof template !== "string" ? {
            file: "file0",
            fields: generateTemplateFieldData(template, fields),
            rawFields: overrideMetadata,
            options
        } : {
            template: template,
            fields: generateTemplateFieldData(template, fields),
            rawFields: overrideMetadata,
            options
        }
    }

    params.set("slave", slave)
    params.set("queue", JSON.stringify(queue));

    if (authenticationService.currentlyLoggedInUser) {
        params.set("user", authenticationService.currentlyLoggedInUser.email);
    }

    if (typeof template !== "string") {
        params.set("file0", template);
    }

    const response = await fetch(`${baseUrl}api/slave/print/`, {
        body: params,
        method: "POST",
        credentials: "include"
    });

    if (!response.ok) {
        const info = await response.text();
        console.error(new Error(info));
        throw info;
    }

    return true;
}

export async function slaveInfo(slave: string) {
    let result;

    try {
        result = await egzotechAgentService.labelPrinter.getPrinterInfo(slave);
    }
    catch(ex) {
        return null;
    }

    return result;
}

export function checkIfCanPrint(metadata: { [key: string]: string }, printerInfo: PrinterInfo) {
    if (!printerInfo || !printerInfo.portName) {
        return "no-printer";
    }

    if (!metadata["tape-width"]) {
        return true;
    }

    const width = parseInt(metadata["tape-width"]);

    if (isNaN(width)) {
        return true;
    }

    if (width === printerInfo.mediaWidth) {
        return true;
    }

    return "invalid-tape-width";
}


function setTemplateFields(template: string | File, fields: LabelTemplateFields | null, params: URLSearchParams | FormData, coerceFields = true) {
    const data = coerceFields ? generateTemplateFieldData(template, fields) : fields;

    if (!data) {
        return;
    }

    for (const prop in data) {
        params.set(prop, data[prop].toString());
    }
}

function generateTemplateFieldData(template: string | File | Blob, fields: LabelTemplateFields | null) {
    const result: { [key: string]: string } = {};

    if (typeof template === "string") {
        result.template = template;
    }

    for (const prop in fields) {
        let value = fields[prop];

        if (value instanceof Date) {
            value = (value.getTime() / 1000).toFixed(0);
        }

        if (typeof value === "number") {
            value = value.toString();
        }

        if (prop.startsWith("field-")) {
            result[prop] = value;
        }
    }

    return result;
}
